/**
 * RelatedPages — sidebar section showing pages related to the active page
 * through shared concepts. Fetches from the /related endpoint and displays
 * with strength indicators and concept tags.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pagesApi from '../../api/pages-api';
import type { RelatedPage } from '../../api/pages-api';

interface RelatedPagesProps {
  readonly pageId: string;
}

export function RelatedPages({ pageId }: RelatedPagesProps): React.ReactElement {
  const navigate = useNavigate();
  const [related, setRelated] = useState<RelatedPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      try {
        const response = await pagesApi.getRelatedPages(pageId);
        if (!cancelled) {
          setRelated(response.data ?? []);
        }
      } catch {
        if (!cancelled) {
          setRelated([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [pageId]);

  return (
    <section aria-label="Related pages" className="border-t">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:bg-accent/30 transition-colors"
        aria-expanded={expanded}
      >
        <span>Related Pages</span>
        <svg
          className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-3 pb-3">
          {loading && (
            <p className="text-xs text-muted-foreground py-2">Loading...</p>
          )}

          {!loading && related.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">
              No related pages yet. Relationships build as more content is added.
            </p>
          )}

          {!loading && related.length > 0 && (
            <ul className="space-y-2" role="list">
              {related.map((page) => (
                <li key={page.pageId}>
                  <button
                    onClick={() => navigate(`/pages/${page.pageId}`)}
                    className="w-full text-left rounded-md p-2 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium truncate">{page.pageTitle}</p>
                      <div
                        className="ml-2 h-1.5 w-8 rounded-full bg-muted overflow-hidden shrink-0"
                        title={`Strength: ${Math.round(page.strength * 100)}%`}
                      >
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${Math.round(page.strength * 100)}%` }}
                        />
                      </div>
                    </div>
                    {page.sharedConcepts.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {page.sharedConcepts.slice(0, 4).map((concept) => (
                          <span
                            key={concept}
                            className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-accent text-accent-foreground"
                          >
                            {concept}
                          </span>
                        ))}
                        {page.sharedConcepts.length > 4 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{page.sharedConcepts.length - 4} more
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
